import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { WebComponent } from "./webComponent";

/**
 * Global debug settings that can be set on client application init
 */
export const webComponentDebugDefaultSettings = {
    /** 
     * used by customElement.decorator : writes a message in console if refresh is called before init is done 
     * ! SHOULD NOT BE USED IN PRODUCTION since it could generate a lot of traces
     * this is the default value for all WebComponents
     * */
    traceRefreshBeforeInitDone: false,
    /** 
     * used by customElement.decorator : writes a message in console if refresh is called twice in less than "threshold" milliseconds (undefined = inactive) 
     * ! SHOULD NOT BE USED IN PRODUCTION since it could generate a lot of traces
     * this is the default value for all WebComponents
     */
    traceDoubleRefreshThresholdMs: <number | undefined>undefined,
    /** 
     * used by customElement.decorator : writes a message in console if init is too long (undefined = inactive)
     * this is the default value for WebPageComponents
     */
    pages_traceLongInitThresholdMs: <number | undefined>1000
};


// name of the property searched by customElement.decorator in WebComponent class
export const TRACE_REFRESH_BEFORE_INIT_DONE = "_traceRefreshBeforeInitDone";
// name of the property searched by customElement.decorator in WebComponent class
export const TRACE_DOUBLE_REFRESH_THRESHOLD_MS = "_traceDoubleRefreshThresholdMs";
// name of the property searched by customElement.decorator in WebComponent class
export const TRACE_LONG_INIT_THRESHOLD_MS = "_traceLongInitThresholdMs";

/**
 * Configure the component with default debug options
 */
export function configureComponentWithDefaultDebugOptions(c: WebComponent): void {

    // refresh should not be called before init is finished
    if (webComponentDebugDefaultSettings.traceRefreshBeforeInitDone) {
        enableTraceRefreshCallBeforeInitDone(c);
    }

    // refresh should not be called several times in a row
    if (webComponentDebugDefaultSettings.traceDoubleRefreshThresholdMs && webComponentDebugDefaultSettings.traceDoubleRefreshThresholdMs > 0) {
        enableTraceDoubleRefresh(c, webComponentDebugDefaultSettings.traceDoubleRefreshThresholdMs);
    }
}

/**
 * Configure the page with default debug options
 */
export function configurePageWithDefaultDebugOptions(page: WebComponent): void {
    // pages init function should not take long
    if (webComponentDebugDefaultSettings.pages_traceLongInitThresholdMs && webComponentDebugDefaultSettings.pages_traceLongInitThresholdMs > 0) {
        enableTraceLongInit(page, webComponentDebugDefaultSettings.pages_traceLongInitThresholdMs);
    }
}

/**
 * writes a message in console if refresh is called before init is done
 */
export function enableTraceRefreshCallBeforeInitDone(c: WebComponent): void {
    setProp(c, TRACE_REFRESH_BEFORE_INIT_DONE, true);
}

/**
 * disable trace if it was enabled for component
 */
export function disableTraceRefreshCallBeforeInitDone(c: WebComponent): void {
    delete c[TRACE_REFRESH_BEFORE_INIT_DONE as keyof WebComponent];
}

/**
 * writes a message in console if init is too long
 */
export function enableTraceLongInit(c: WebComponent, thresholdMs: number): void {
    setProp(c, TRACE_LONG_INIT_THRESHOLD_MS, thresholdMs);
}

/**
 * disable trace if it was enabled for component
 */
export function disableTraceLongInit(c: WebComponent): void {
    delete c[TRACE_LONG_INIT_THRESHOLD_MS as keyof WebComponent];
}

/**
 * writes a message in console if refresh is called twice in less than "threshold" milliseconds 
 */
export function enableTraceDoubleRefresh(c: WebComponent, thresholdMs: number): void {
    setProp(c, TRACE_DOUBLE_REFRESH_THRESHOLD_MS, thresholdMs);
}

/**
 * disable trace if it was enabled for component
 */
export function disableTraceDoubleRefresh(c: WebComponent): void {
    delete c[TRACE_DOUBLE_REFRESH_THRESHOLD_MS as keyof WebComponent];
}